import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Award, Download, Calendar, Clock, AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export function MyAssignments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/my/assignments");
        setItems(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (items.length === 0) return null;

  const today = new Date();
  return (
    <div className="mb-10" data-testid="my-assignments-section">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-1">Asignado a ti</p>
          <h2 className="font-heading text-2xl text-[#1A1A1A]">Capacitación obligatoria</h2>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((a) => {
          let dueLabel = null;
          let dueColor = "text-[#737373]";
          let icon = Calendar;
          if (a.due_date && a.status === "pending") {
            const d = new Date(a.due_date);
            const days = Math.ceil((d - today) / 86400000);
            if (days < 0) {
              dueLabel = `Vencido hace ${Math.abs(days)} día(s)`;
              dueColor = "text-[#DC2626]";
              icon = AlertTriangle;
            } else if (days <= 3) {
              dueLabel = `Vence en ${days} día(s)`;
              dueColor = "text-[#EAB308]";
              icon = Clock;
            } else {
              dueLabel = `Vence el ${d.toLocaleDateString("es-MX")}`;
            }
          }
          const Icon = icon;
          const link = a.item_type === "course" ? `/courses/${a.item_id}` : "/dashboard";
          return (
            <Link
              key={a.id}
              to={link}
              data-testid={`assignment-item-${a.id}`}
              className="group bg-white border border-[#E5E7EB] hover:border-[#E5E7EB] rounded-md p-5 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">
                    {a.item_type === "path" ? "Ruta de capacitación" : "Curso obligatorio"}
                  </p>
                  <h3 className="font-heading text-lg text-[#1A1A1A] mt-0.5">{a.item_title}</h3>
                </div>
                {a.status === "completed" ? (
                  <span className="inline-flex items-center gap-1 text-xs text-[#1B2E7A] bg-[#1B2E7A]/10 px-2 py-1 rounded-md">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Completado
                  </span>
                ) : (
                  <ChevronRight className="h-5 w-5 text-[#6B7280] group-hover:translate-x-0.5 transition-transform" />
                )}
              </div>
              {a.item_type === "path" && a.total_courses != null && (
                <div className="mt-2">
                  <div className="h-1.5 rounded-full bg-[#E5E7EB] overflow-hidden">
                    <div
                      className="h-full bg-[#1B2E7A] transition-all duration-500"
                      style={{ width: `${a.total_courses ? (a.passed_courses / a.total_courses) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="text-xs text-[#737373] mt-1">
                    {a.passed_courses}/{a.total_courses} cursos aprobados
                  </p>
                </div>
              )}
              {a.item_type === "course" && a.best_score != null && (
                <p className={`text-xs mt-2 ${a.passed ? "text-[#1B2E7A]" : "text-[#DC2626]"} font-medium`}>
                  Mejor puntaje: {a.best_score}% {a.passed ? "· Aprobado" : ""}
                </p>
              )}
              {dueLabel && (
                <p className={`text-xs mt-3 inline-flex items-center gap-1 ${dueColor}`}>
                  <Icon className="h-3.5 w-3.5" /> {dueLabel}
                </p>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function MyCertificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/certificates/me");
        setCerts(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (certs.length === 0) return null;

  return (
    <div className="mb-10" data-testid="my-certificates-section">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-1">Tus logros</p>
          <h2 className="font-heading text-2xl text-[#1A1A1A]">Mis certificados</h2>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {certs.map((c) => (
          <div
            key={c.id}
            data-testid={`cert-card-${c.id}`}
            className={`relative overflow-hidden border rounded-md p-5 ${
              c.certificate_type === "master"
                ? "bg-gradient-to-br from-[#FFF6E6] to-[#FBEBD0] border-[#E5C580]"
                : "bg-white border-[#E5E7EB]"
            }`}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className={`h-10 w-10 rounded-md flex items-center justify-center ${c.certificate_type === "master" ? "bg-[#EAB308] text-white" : "bg-[#1B2E7A] text-white"}`}>
                <Award className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">
                  {c.certificate_type === "master" ? "Certificado Maestro" : "Certificado de Curso"}
                </p>
                <h4 className="font-heading text-base text-[#1A1A1A] mt-0.5 leading-tight">
                  {c.course_title}
                </h4>
              </div>
            </div>
            {c.score != null && (
              <p className="text-xs text-[#374151]">Calificación: <strong>{c.score}%</strong></p>
            )}
            <p className="text-xs text-[#737373] mt-1">
              Emitido el {new Date(c.issued_at).toLocaleDateString("es-MX")}
            </p>
            <a
              href={`${API}/certificates/${c.id}/download`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-sm text-[#1B2E7A] hover:underline"
              data-testid={`download-my-cert-${c.id}`}
            >
              <Download className="h-3.5 w-3.5" /> Descargar PDF
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}
